"use client";

import { useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { primeClarityMetadata } from "@/lib/clarityMetadata";

/**
 * Microsoft Clarity loads asynchronously through `next/script` (lazyOnload),
 * so on first paint `window.clarity` may not exist yet. Poll briefly until it
 * does, prime the metadata cache so other consumers (GTM payloads) can read
 * `userId`/`sessionId`/`projectId` synchronously, and tag the session with our
 * own customer id when the user is logged in.
 *
 * The retry loop stops on first success or after MAX_WAIT_MS to avoid leaks.
 */
const POLL_INTERVAL_MS = 500;
const MAX_WAIT_MS = 15_000;

export function ClarityIdentify() {
  const { user, isReady } = useAuth();
  const customerId = user?.customerId;

  useEffect(() => {
    if (!isReady) return;
    if (typeof window === "undefined") return;

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const startedAt = Date.now();

    const tryAttach = () => {
      if (cancelled) return;
      const clarity = window.clarity;
      if (typeof clarity === "function") {
        primeClarityMetadata();
        if (customerId) {
          try {
            clarity("set", "UserId", customerId);
          } catch (err) {
            console.error("Clarity set UserId failed:", err);
          }
        }
        return;
      }
      if (Date.now() - startedAt > MAX_WAIT_MS) return;
      timer = setTimeout(tryAttach, POLL_INTERVAL_MS);
    };

    tryAttach();

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [isReady, customerId]);

  return null;
}
